import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import API from "@/lib/api";
import { DialogBody } from "@/components/shared/DialogBody";
import { HealthScore } from "@/components/shared/HealthScore";
import { Zap, Library, Settings, LogOut, User, ChevronDown, ChevronRight, Sun, Moon, Plus } from "lucide-react";

export function Header({ projectName }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const [creating, setCreating] = useState(false);

  const isOnDashboard = location.pathname === "/dashboard";
  const isOnLibrary = location.pathname === "/library";
  const isOnPacks = location.pathname === "/packs";
  const isInProject = location.pathname.startsWith("/projects/");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const { data } = await API.post("/projects", newProject);
      setNewProjectOpen(false);
      setNewProject({ name: "", description: "" });
      navigate(`/projects/${data.id}`);
    } catch (err) {
      console.error("Create project error:", err);
    } finally {
      setCreating(false);
    }
  };

  return (
    <header className="bg-[var(--pf-bg)] border-b border-[var(--pf-border)] sticky top-0 z-50" data-testid="app-header">
      <div className="max-w-[1400px] mx-auto px-6 h-12 flex items-center justify-between">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-1">
          <Link to="/dashboard" className="flex items-center gap-2 mr-4" data-testid="header-logo">
            <div className="w-6 h-6 bg-[var(--pf-accent)] flex items-center justify-center">
              <Zap className="w-3 h-3 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading text-base font-bold tracking-tight text-[var(--pf-text)] hidden sm:inline">staqblox</span>
          </Link>

          <Link to="/dashboard" data-testid="nav-dashboard"
            className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${isOnDashboard ? "bg-[var(--pf-accent)] text-white" : "text-[var(--pf-text-secondary)] hover:bg-[var(--pf-surface)] hover:text-[var(--pf-text)]"}`}>
            Projects
          </Link>
          <Link to="/library" data-testid="nav-library"
            className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${isOnLibrary ? "bg-[var(--pf-accent)] text-white" : "text-[var(--pf-text-secondary)] hover:bg-[var(--pf-surface)] hover:text-[var(--pf-text)]"}`}>
            Library
          </Link>
          <Link to="/packs" data-testid="nav-packs"
            className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${isOnPacks ? "bg-[var(--pf-accent)] text-white" : "text-[var(--pf-text-secondary)] hover:bg-[var(--pf-surface)] hover:text-[var(--pf-text)]"}`}>
            Packs
          </Link>

          {/* Project breadcrumb */}
          {isInProject && projectName && (
            <div className="flex items-center gap-1 ml-1" data-testid="project-breadcrumb">
              <ChevronRight className="w-3 h-3 text-[var(--pf-text-muted)]" />
              <span className="px-2 py-1 text-[13px] font-semibold text-[var(--pf-text)] bg-[var(--pf-surface)] truncate max-w-[220px]">{projectName}</span>
            </div>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1.5">
          <HealthScore testId="header-health" />

          <button
            onClick={() => document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))}
            data-testid="cmdk-trigger"
            title="Open command palette (⌘K)"
            className="hidden md:flex items-center gap-1.5 h-7 px-2 text-[11px] font-mono text-[var(--pf-text-muted)] hover:text-[var(--pf-text)] hover:bg-[var(--pf-surface)] transition-colors border border-[var(--pf-border)]">
            <span>Quick nav</span>
            <kbd className="text-[10px] px-1 py-0.5 bg-[var(--pf-surface)] border border-[var(--pf-border)]">⌘K</kbd>
          </button>

          <Button onClick={() => setNewProjectOpen(true)} data-testid="header-new-project"
            className="rounded-none bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white h-7 px-3 text-[11px] font-medium">
            <Plus className="w-3 h-3 mr-1" />New Playbook
          </Button>

          <Button variant="ghost" size="sm" onClick={toggleTheme} data-testid="theme-toggle"
            className="rounded-none hover:bg-[var(--pf-surface)] h-7 w-7 p-0"
            title={theme === "dark" ? "Light mode" : "Dark mode"}>
            {theme === "dark" ? <Sun className="w-3.5 h-3.5 text-[var(--pf-text-secondary)]" /> : <Moon className="w-3.5 h-3.5 text-[var(--pf-text-secondary)]" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" data-testid="user-menu-trigger"
                className="flex items-center gap-1.5 rounded-none hover:bg-[var(--pf-surface)] h-7 px-2">
                <div className="w-5 h-5 bg-[var(--pf-accent)] flex items-center justify-center">
                  <User className="w-2.5 h-2.5 text-white" />
                </div>
                <span className="text-[12px] font-medium hidden sm:inline text-[var(--pf-text)]">{user?.name || "Account"}</span>
                <ChevronDown className="w-2.5 h-2.5 text-[var(--pf-text-muted)]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-none border-[var(--pf-border)]">
              <div className="px-3 py-2 text-xs text-[var(--pf-text-secondary)]">{user?.email}</div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings" className="rounded-none cursor-pointer">
                <Settings className="w-3.5 h-3.5 mr-2" />Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout" className="rounded-none cursor-pointer text-red-600 focus:text-red-600">
                <LogOut className="w-3.5 h-3.5 mr-2" />Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* New Project Dialog */}
      <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
        <DialogContent className="rounded-none border-[var(--pf-border)] sm:max-w-md">
<DialogBody>
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Create Playbook</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">Playbook Name</Label>
              <Input
                value={newProject.name}
                onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                placeholder="e.g. Invoice Generator SaaS"
                required
                data-testid="header-project-name"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] h-10"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] font-medium text-[var(--pf-text-secondary)] mb-2 block">Description</Label>
              <Textarea
                value={newProject.description}
                onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                placeholder="Brief description..."
                data-testid="header-project-description"
                className="rounded-none border-[var(--pf-border)] focus:ring-2 focus:ring-[var(--pf-accent)] resize-none"
                rows={3}
              />
            </div>
            <Button type="submit" disabled={creating} data-testid="header-submit-project"
              className="w-full bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white rounded-none h-10 font-medium">
              {creating ? "Creating..." : "Create Playbook"}
            </Button>
          </form>
</DialogBody>
</DialogContent>
      </Dialog>
    </header>
  );
}
